import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import { useLocations } from '../../../hooks/useLocations'; // Add this import
import { App } from './AppManager';
import AddStore from './AddStore';
import RequestMoreStores from './RequestMoreStores';
import StripeConnectionModal from './StripeConnectionModal';
import { getLocationById } from './config/locationConfig';
import '../../../styles/StoreManagement.css';

// Store interface with test/live Stripe support
interface Store {
  id?: string;
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  website?: string;
  timezone: string;
  currency: string;
  // Test environment Stripe details
  stripeTestAccountId?: string;
  stripeTestAccountName?: string;
  stripeTestConnected: boolean;
  stripeTestConnectUrl?: string;
  stripeTestConnectUrlExpires?: Date | any;
  stripeTestOnboardingStarted?: Date | any;
  // Live environment Stripe details
  stripeLiveAccountId?: string;
  stripeLiveAccountName?: string;
  stripeLiveConnected: boolean;
  stripeLiveConnectUrl?: string;
  stripeLiveConnectUrlExpires?: Date | any;
  stripeLiveOnboardingStarted?: Date | any;
  // Legacy fields for backward compatibility
  stripeAccountId?: string;
  stripeAccountName?: string;
  stripeConnected: boolean;
  stripeConnectUrl?: string;
  stripeConnectUrlExpires?: Date | any;
  stripeOnboardingStarted?: Date | any;
  isActive: boolean;
  createdAt: Date | any;
  updatedAt: Date | any;
}

interface StoreRequest {
  id?: string;
  appId: string;
  organizationId: string;
  appName: string;
  requestedBy: string;
  requestedAt: Date;
  requestType: 'add-more-stores';
  currentStoreCount: number;
  requestedStoreCount: number;
  justification: string;
  status: 'pending' | 'approved' | 'rejected';
}

interface StoreManagementProps {
  db: Firestore;
  functions: Functions;
  appData: App;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  onBack: () => void;
  selectedOrganizationId: string;
  onBackToOrganizations: () => void;
  onViewAdminPanel?: () => void;
  onViewPermissions?: () => void;
}

// Store Details Modal Component
const StoreDetailsModal: React.FC<{
  store: Store;
  onClose: () => void;
  onEdit: () => void;
  onConnectStripeTest: () => void;
  onConnectStripeLive: () => void;
}> = ({ store, onClose, onEdit, onConnectStripeTest, onConnectStripeLive }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container store-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Store Details</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="store-details-header">
            <h4 className="store-details-title">{store.name}</h4>
            <div className="store-details-status">
              <span className={`storemanagement-status-indicator ${store.isActive ? 'storemanagement-active' : 'storemanagement-inactive'}`}>
                {store.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="store-details-sections">
            <div className="store-details-section">
              <h4>Basic Information</h4>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Store ID:</span>
                <span className="storemanagement-detail-value">{store.id}</span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Description:</span>
                <span className="storemanagement-detail-value">{store.description}</span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Website:</span>
                <span className="storemanagement-detail-value">{store.website || 'Not provided'}</span>
              </div>
            </div>

            <div className="store-details-section">
              <h4>Location & Contact</h4>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Address:</span>
                <span className="storemanagement-detail-value">{store.address}</span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">City:</span>
                <span className="storemanagement-detail-value">{store.city}</span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">State/Province:</span>
                <span className="storemanagement-detail-value">{store.state}</span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">ZIP/Postal Code:</span>
                <span className="storemanagement-detail-value">{store.zipCode}</span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Country:</span>
                <span className="storemanagement-detail-value">{store.country}</span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Phone:</span>
                <span className="storemanagement-detail-value">{store.phone}</span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Email:</span>
                <span className="storemanagement-detail-value">{store.email}</span>
              </div>
            </div>

            <div className="store-details-section">
              <h4>Settings & Payment</h4>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Timezone:</span>
                <span className="storemanagement-detail-value">{store.timezone}</span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Currency:</span>
                <span className="storemanagement-detail-value">{store.currency}</span>
              </div>
              
              {store.stripeTestAccountName && (
                <div className="storemanagement-detail-row">
                  <span className="storemanagement-detail-label">Test Stripe Account:</span>
                  <span className="storemanagement-detail-value">{store.stripeTestAccountName}</span>
                </div>
              )}
              
              {store.stripeLiveAccountName && (
                <div className="storemanagement-detail-row">
                  <span className="storemanagement-detail-label">Live Stripe Account:</span>
                  <span className="storemanagement-detail-value">{store.stripeLiveAccountName}</span>
                </div>
              )}
              
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Created:</span>
                <span className="storemanagement-detail-value">
                  {store.createdAt instanceof Date ? store.createdAt.toLocaleDateString() : 'Unknown'}
                </span>
              </div>
              <div className="storemanagement-detail-row">
                <span className="storemanagement-detail-label">Last Updated:</span>
                <span className="storemanagement-detail-value">
                  {store.updatedAt instanceof Date ? store.updatedAt.toLocaleDateString() : 'Unknown'}
                </span>
              </div>
            </div>
          </div>

          <div className="store-actions-section">
            <button type="button" className="storemanagement-edit-store-btn" onClick={onEdit}>
              Edit Store
            </button>
            
            <div className="stripe-environment-section">
              <h5>Test Environment</h5>
              {!store.stripeTestConnected ? (
                <button 
                  type="button" 
                  className="storemanagement-connect-stripe-btn storemanagement-primary"
                  onClick={onConnectStripeTest}
                >
                  Connect Test Stripe Account
                </button>
              ) : (
                <button 
                  type="button" 
                  className="storemanagement-manage-stripe-btn"
                  onClick={onConnectStripeTest}
                >
                  Manage Test Stripe Account
                </button>
              )}
            </div>
            
            <div className="stripe-environment-section">
              <h5>Live Environment</h5>
              {!store.stripeLiveConnected ? (
                <button 
                  type="button" 
                  className="storemanagement-connect-stripe-btn storemanagement-primary"
                  onClick={onConnectStripeLive}
                >
                  Connect Live Stripe Account
                </button>
              ) : (
                <button 
                  type="button" 
                  className="storemanagement-manage-stripe-btn"
                  onClick={onConnectStripeLive}
                >
                  Manage Live Stripe Account
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const StoreManagement: React.FC<StoreManagementProps> = ({
  db,
  functions,
  appData,
  onError,
  onSuccess,
  setLoading,
  onBack,
  selectedOrganizationId,
  onBackToOrganizations,
  onViewAdminPanel
}) => {
  const { currentUser } = useAuth();
  
  // ADD THIS LINE - Use the locations hook
  const { getOrganizationById } = useLocations();
  
  const [stores, setStores] = useState<Store[]>([]);
  const [showAddStore, setShowAddStore] = useState(false);
  const [showRequestMoreStores, setShowRequestMoreStores] = useState(false);
  const [showStripeConnection, setShowStripeConnection] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [showStoreDetails, setShowStoreDetails] = useState(false);
  const [stripeEnvironment, setStripeEnvironment] = useState<'test' | 'live'>('test');

  const submitStoreRequestFunc = httpsCallable(functions, 'submitStoreRequest');
  const createStoreFunc = httpsCallable(functions, 'createStore');

  // ADD THIS HELPER FUNCTION - Get the correct location ID
  const getCorrectLocationId = (): string => {
    // First try to get location from the selected organization
    if (selectedOrganizationId) {
      const orgLocation = getOrganizationById(selectedOrganizationId);
      if (orgLocation) {
        console.log('Using organization location:', orgLocation.id, 'for org:', selectedOrganizationId);
        return orgLocation.id;
      }
    }
    
    // Fallback to app's locationId if it exists
    if (appData.locationId) {
      console.log('Using app locationId:', appData.locationId);
      return appData.locationId;
    }
    
    // Final fallback
    console.log('Using fallback location: uk-london');
    return 'uk-london';
  };

  // ADD DEBUGGING - Add this useEffect to see what's happening
  useEffect(() => {
    console.log('StoreManagement Debug Info:', {
      selectedOrganizationId,
      appDataLocationId: appData.locationId,
      appDataOrganizationId: appData.organizationId,
      correctLocationId: getCorrectLocationId()
    });
  }, [selectedOrganizationId, appData.locationId, appData.organizationId]);

  const loadStores = async () => {
    try {
      const q = query(
        collection(db, 'stores'),
        where('appId', '==', appData.id)
      );
      const querySnapshot = await getDocs(q);
      
      const storesList: Store[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as Store;
        
        let createdAt: Date;
        if (data.createdAt instanceof Timestamp) {
          createdAt = data.createdAt.toDate();
        } else if (data.createdAt && typeof data.createdAt === 'object' && 'toDate' in data.createdAt) {
          createdAt = (data.createdAt as any).toDate();
        } else {
          createdAt = new Date(data.createdAt);
        }
        
        let updatedAt: Date;
        if (data.updatedAt instanceof Timestamp) {
          updatedAt = data.updatedAt.toDate();
        } else if (data.updatedAt && typeof data.updatedAt === 'object' && 'toDate' in data.updatedAt) {
          updatedAt = (data.updatedAt as any).toDate();
        } else {
          updatedAt = new Date(data.updatedAt);
        }
        
        storesList.push({
          ...data,
          id: doc.id,
          createdAt: createdAt,
          updatedAt: updatedAt
        });
      });

      setStores(storesList);
    } catch (err) {
      console.error('Error loading stores:', err);
      onError('Failed to load stores.');
    }
  };

  useEffect(() => {
    const initializeStores = async () => {
      setLoading(true);
      await loadStores();
      setLoading(false);
    };

    initializeStores();
  }, [appData.id]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const accountId = urlParams.get('account_id');
    const storeId = urlParams.get('store_id');
    const appId = urlParams.get('app_id');
    const environment = urlParams.get('environment') as 'test' | 'live' || 'test';

    if (accountId && storeId && appId && appId === appData.id) {
      handleStripeReturn(accountId, storeId, appId, environment);
    }
  }, [appData.id]);

  const handleStripeReturn = async (accountId: string, storeId: string, appId: string, environment: 'test' | 'live') => {
    setLoading(true);
    try {
      const completeStoreSetupFunc = httpsCallable(functions, 'completeStoreSetup');
      
      const result = await completeStoreSetupFunc({
        accountId,
        storeId,
        appId,
        environment
      });

      // @ts-ignore
      if (result.data && result.data.success) {
        // @ts-ignore
        onSuccess(result.data.message);
        await loadStores();
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // @ts-ignore
        onError(result.data?.message || 'Store setup could not be completed');
      }
    } catch (err: any) {
      console.error('Error completing store setup:', err);
      onError(err.message || 'Failed to complete store setup');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStore = async (storeData: Store) => {
    if (!currentUser?.email) {
      onError('User email not available.');
      return;
    }

    setLoading(true);
    try {
      const result = await createStoreFunc({ 
        storeData: storeData,
        appId: appData.id,
        organizationId: appData.organizationId
      });

      // @ts-ignore
      if (result.data && result.data.success) {
        await loadStores();
        setShowAddStore(false);
        // @ts-ignore
        onSuccess(result.data.message || `Store "${storeData.name}" has been created successfully!`);
      } else {
        // @ts-ignore
        onError(result.data?.message || 'Failed to create store.');
      }
    } catch (err: any) {
      console.error('Error creating store:', err);
      onError(err.message || 'Failed to create store.');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreSubmit = (storeData: Store) => {
    handleCreateStore(storeData).catch((err) => {
      console.error('Error in store creation:', err);
    });
  };

  const canAddMoreStores = () => {
    if (appData.type === 'single-store') {
      return stores.length === 0;
    }
    return true;
  };

  const getMaxStores = () => {
    switch (appData.type) {
      case 'single-store':
        return 1;
      case 'single-store-multi-location':
        return 10;
      case 'multi-store':
        return 100;
      default:
        return 1;
    }
  };

  const handleConnectStripe = (store: Store, environment: 'test' | 'live') => {
    setSelectedStore(store);
    setStripeEnvironment(environment);
    setShowStripeConnection(true);
  };

  const handleViewStoreDetails = (store: Store) => {
    setSelectedStore(store);
    setShowStoreDetails(true);
  };

  const handleRequestMoreStores = async (justification: string, requestedCount: number) => {
    if (!currentUser?.email) {
      onError('User email not available.');
      return;
    }

    setLoading(true);
    try {
      const requestData: StoreRequest = {
        appId: appData.id,
        organizationId: appData.organizationId,
        appName: appData.name,
        requestedBy: currentUser.email,
        requestedAt: new Date(),
        requestType: 'add-more-stores',
        currentStoreCount: stores.length,
        requestedStoreCount: requestedCount,
        justification: justification,
        status: 'pending'
      };

      const result = await submitStoreRequestFunc({ requestData });

      // @ts-ignore
      if (result.data && result.data.success) {
        onSuccess('Request submitted successfully. An admin will review it shortly.');
        setShowRequestMoreStores(false);
      } else {
        // @ts-ignore
        onError(result.data?.message || 'Failed to submit request.');
      }
    } catch (err: any) {
      console.error('Error submitting store request:', err);
      onError(err.message || 'Failed to submit request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="applist-content-header">
        <h1 className="applist-page-title">Store Management: {appData.name}</h1>
      </div>

      <div className="applist-content-body">
        <div className="storemanagement-content">
          <div className="storemanagement-header">
            <div className="storemanagement-info">
              <h3>Your Stores</h3>
              <div className="storemanagement-count-info">
                <span className="storemanagement-store-count">{stores.length}</span>
                <span className="storemanagement-store-limit">/ {getMaxStores()}</span>
                <span className="storemanagement-store-type">({appData.type})</span>
              </div>
            </div>

            <div className="storemanagement-actions">
              {canAddMoreStores() ? (
                <button
                  type="button"
                  className="storemanagement-add-store-btn"
                  onClick={() => setShowAddStore(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Store
                </button>
              ) : appData.type === 'single-store' ? (
                <button
                  type="button"
                  className="storemanagement-request-more-stores-btn"
                  onClick={() => setShowRequestMoreStores(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 9V5a3 3 0 0 0-6 0v4"/>
                    <rect x="2" y="9" width="20" height="11" rx="2" ry="2"/>
                  </svg>
                  Request More Stores
                </button>
              ) : null}
            </div>
          </div>

          {stores.length === 0 ? (
            <div className="storemanagement-empty-stores-state">
              <div className="storemanagement-empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9,22 9,12 15,12 15,22"/>
                </svg>
              </div>
              <h4>No Stores Added Yet</h4>
              <p>Your app needs at least one store to be complete. Add your first store to get started.</p>
              <button
                type="button"
                className="storemanagement-add-first-store-btn"
                onClick={() => setShowAddStore(true)}
              >
                Add Your First Store
              </button>
            </div>
          ) : (
            <>
              <div className="storemanagement-table-container">
                <table className="storemanagement-table">
                  <thead>
                    <tr>
                      <th>Store Name</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Stripe Test</th>
                      <th>Stripe Live</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.map((store) => (
                      <tr 
                        key={store.id} 
                        className="storemanagement-table-row"
                        onClick={() => handleViewStoreDetails(store)}
                      >
                        <td className="store-name-cell">
                          <div className="store-name-content">
                            <span className="store-name">{store.name}</span>
                            <span className="store-id">ID: {store.id}</span>
                          </div>
                        </td>
                        <td className="store-location-cell">
                          <div className="store-location-content">
                            <span className="store-city">{store.city}, {store.state}</span>
                            <span className="store-country">{store.country}</span>
                          </div>
                        </td>
                        <td className="store-status-cell">
                          <span className={`storemanagement-status-indicator ${store.isActive ? 'storemanagement-active' : 'storemanagement-inactive'}`}>
                            {store.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="store-stripe-test-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="stripe-column-content">
                            {!store.stripeTestConnected ? (
                              <button 
                                type="button" 
                                className="storemanagement-connect-stripe-btn storemanagement-test"
                                onClick={() => handleConnectStripe(store, 'test')}
                                title="Connect Test Stripe Account"
                              >
                                Connect Test
                              </button>
                            ) : (
                              <div className="stripe-connected-info">
                                <span className="stripe-status-badge test-connected">✓ Connected</span>
                                <button 
                                  type="button" 
                                  className="storemanagement-manage-stripe-btn storemanagement-test-connected"
                                  onClick={() => handleConnectStripe(store, 'test')}
                                  title="Manage Test Stripe Account"
                                >
                                  Manage
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="store-stripe-live-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="stripe-column-content">
                            {!store.stripeLiveConnected ? (
                              <button 
                                type="button" 
                                className="storemanagement-connect-stripe-btn storemanagement-live"
                                onClick={() => handleConnectStripe(store, 'live')}
                                title="Connect Live Stripe Account"
                              >
                                Connect Live
                              </button>
                            ) : (
                              <div className="stripe-connected-info">
                                <span className="stripe-status-badge live-connected">✓ Connected</span>
                                <button 
                                  type="button" 
                                  className="storemanagement-manage-stripe-btn storemanagement-live-connected"
                                  onClick={() => handleConnectStripe(store, 'live')}
                                  title="Manage Live Stripe Account"
                                >
                                  Manage
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="store-actions-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="table-actions">
                            <button 
                              type="button"
                              className="storemanagement-view-details-btn"
                              onClick={() => handleViewStoreDetails(store)}
                              title="View store details"
                            >
                              View Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="storemanagement-stores-grid">
                {stores.map((store) => (
                  <div 
                    key={`mobile-${store.id}`} 
                    className="storemanagement-store-card"
                    onClick={() => handleViewStoreDetails(store)}
                  >
                    <div className="storemanagement-store-header">
                      <h4>{store.name}</h4>
                      <div className="storemanagement-store-status">
                        <span className={`storemanagement-status-indicator ${store.isActive ? 'storemanagement-active' : 'storemanagement-inactive'}`}>
                          {store.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mobile-store-summary">
                      <div className="mobile-summary-item">
                        <span className="mobile-summary-label">Store ID:</span>
                        <span className="mobile-summary-value">{store.id}</span>
                      </div>
                      <div className="mobile-summary-item">
                        <span className="mobile-summary-label">Location:</span>
                        <span className="mobile-summary-value">{store.city}, {store.state}</span>
                      </div>
                      <div className="mobile-summary-item">
                        <span className="mobile-summary-label">Phone:</span>
                        <span className="mobile-summary-value">{store.phone}</span>
                      </div>
                    </div>

                    <div className="mobile-stripe-status">
                      <div className="mobile-stripe-section">
                        <span className="mobile-stripe-label">Test:</span>
                        {store.stripeTestConnected ? (
                          <span className="stripe-status-badge test-connected">✓ Connected</span>
                        ) : (
                          <span className="stripe-status-badge not-connected">Not Connected</span>
                        )}
                      </div>
                      <div className="mobile-stripe-section">
                        <span className="mobile-stripe-label">Live:</span>
                        {store.stripeLiveConnected ? (
                          <span className="stripe-status-badge live-connected">✓ Connected</span>
                        ) : (
                          <span className="stripe-status-badge not-connected">Not Connected</span>
                        )}
                      </div>
                    </div>

                    <div className="mobile-store-actions" onClick={(e) => e.stopPropagation()}>
                      <div className="mobile-stripe-buttons">
                        {!store.stripeTestConnected ? (
                          <button 
                            type="button" 
                            className="storemanagement-connect-stripe-btn storemanagement-test"
                            onClick={() => handleConnectStripe(store, 'test')}
                          >
                            Connect Test
                          </button>
                        ) : (
                          <button 
                            type="button" 
                            className="storemanagement-manage-stripe-btn storemanagement-test-connected"
                            onClick={() => handleConnectStripe(store, 'test')}
                          >
                            Manage Test
                          </button>
                        )}
                        
                        {!store.stripeLiveConnected ? (
                          <button 
                            type="button" 
                            className="storemanagement-connect-stripe-btn storemanagement-live"
                            onClick={() => handleConnectStripe(store, 'live')}
                          >
                            Connect Live
                          </button>
                        ) : (
                          <button 
                            type="button" 
                            className="storemanagement-manage-stripe-btn storemanagement-live-connected"
                            onClick={() => handleConnectStripe(store, 'live')}
                          >
                            Manage Live
                          </button>
                        )}
                      </div>
                      
                      <button 
                        type="button"
                        className="storemanagement-view-details-btn mobile-view-details"
                        onClick={() => handleViewStoreDetails(store)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {stores.length === 0 && (
            <div className="storemanagement-app-incomplete-warning">
              <div className="storemanagement-warning-content">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div>
                  <h4>App Incomplete</h4>
                  <p>Your app is not ready to accept orders until you add at least one store with Stripe connections.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showStoreDetails && selectedStore && (
        <StoreDetailsModal
          store={selectedStore}
          onClose={() => {
            setShowStoreDetails(false);
            setSelectedStore(null);
          }}
          onEdit={() => {
            console.log('Edit store:', selectedStore);
          }}
          onConnectStripeTest={() => {
            setShowStoreDetails(false);
            handleConnectStripe(selectedStore, 'test');
          }}
          onConnectStripeLive={() => {
            setShowStoreDetails(false);
            handleConnectStripe(selectedStore, 'live');
          }}
        />
      )}

      {showAddStore && (
        <AddStore
          onSubmit={handleStoreSubmit}
          onCancel={() => setShowAddStore(false)}
          appRegion={appData.region}
          locationId={getCorrectLocationId()} // ← Use the helper function instead
        />
      )}

      {showRequestMoreStores && (
        <RequestMoreStores
          onSubmit={handleRequestMoreStores}
          onCancel={() => setShowRequestMoreStores(false)}
          currentStoreCount={stores.length}
          appName={appData.name}
        />
      )}

      {showStripeConnection && selectedStore && (
        <StripeConnectionModal
          store={selectedStore}
          appId={appData.id}
          locationId={getCorrectLocationId()} // ← Use the helper function here too
          environment={stripeEnvironment}
          db={db}
          functions={functions}
          onSuccess={(message: string) => {
            onSuccess(message);
            loadStores();
          }}
          onError={onError}
          onCancel={() => {
            setShowStripeConnection(false);
            setSelectedStore(null);
          }}
          setLoading={setLoading}
        />
      )}
    </>
  );
};

export default StoreManagement;